import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import bookingService from "../../../../../../services/booking.service";
import { useEyeconxStore } from "../../../../../../store/eyeconxStore";
import Loading from "../../../../../common/Loading";
import Pagination from "../../../../../common/Pagination";

const DoctorCustomHours = ({ doctor }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const userId = useEyeconxStore((state) => state.user.id);
    const [loading, setLoading] = useState(true);
    const [customHours, setCustomHours] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize] = useState(5);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
        defaultValues: {
            isOff: false,
            startTime: '09:00',
            endTime: '17:00',
            lunchFrom: '12:00',
            lunchTo: '13:00'
        }
    });

    const watchIsOff = watch('isOff');

    useEffect(() => {
        getCustomHours();
    }, [pageNo]);

    const getCustomHours = async () => {
        try {
            setLoading(true);
            const res = await bookingService.getCustomHours(clinicId, doctor.id, pageNo, pageSize);
            setCustomHours(res.data.content || []);
            setTotalPages(res.data.totalPages || 0);
            setTotalElements(res.data.totalElements || 0);
        } catch (error) {
            console.log('Error fetching custom hours:', error);
            setCustomHours([]);
        } finally {
            setLoading(false);
        }
    };

    const addCustomHours = async (data) => {
        try {
            setLoading(true);
            const customHourData = {
                doctorId: doctor.id,
                workDate: data.workDate,
                isOff: data.isOff,
                startTime: data.isOff ? null : data.startTime + ':00',
                endTime: data.isOff ? null : data.endTime + ':00',
                lunchFrom: data.isOff ? null : data.lunchFrom + ':00',
                lunchTo: data.isOff ? null : data.lunchTo + ':00',
                userId: doctor.userId,
                clinicId: clinicId
            };

            await bookingService.addCustomHour(clinicId, doctor.id, customHourData);
            await getCustomHours();
            setAddingNew(false);
            reset();
        } catch (error) {
            console.log('Error adding custom hour:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateCustomHours = async (id, data) => {
        try {
            setLoading(true);
            const customHourData = {
                id: id,
                doctorId: doctor.id,
                workDate: data.workDate,
                isOff: data.isOff,
                startTime: data.isOff ? null : data.startTime + ':00',
                endTime: data.isOff ? null : data.endTime + ':00',
                lunchFrom: data.isOff ? null : data.lunchFrom + ':00',
                lunchTo: data.isOff ? null : data.lunchTo + ':00',
                userId: doctor.userId,
                clinicId: clinicId
            };

            await bookingService.addCustomHour(clinicId, doctor.id, customHourData);
            await getCustomHours();
            setEditingId(null);
        } catch (error) {
            console.log('Error updating custom hour:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteCustomHours = async (id) => {
        try {
            setLoading(true);
            await bookingService.deleteCustomHour(clinicId, doctor.id, id);
            await getCustomHours();
            setDeleteConfirm(null);
        } catch (error) {
            console.log('Error deleting custom hour:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (customHour) => {
        setEditingId(customHour.id);
        setValue('workDate', customHour.workDate);
        setValue('isOff', customHour.isOff);
        setValue('startTime', customHour.startTime?.slice(0, 5) || '09:00');
        setValue('endTime', customHour.endTime?.slice(0, 5) || '17:00');
        setValue('lunchFrom', customHour.lunchFrom?.slice(0, 5) || '12:00');
        setValue('lunchTo', customHour.lunchTo?.slice(0, 5) || '13:00');
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({
            workDate: '',
            isOff: false,
            startTime: '09:00',
            endTime: '17:00',
            lunchFrom: '12:00',
            lunchTo: '13:00'
        });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            weekday: 'short'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '-';
        return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    if (loading && customHours.length === 0) {
        return <Loading />;
    }

    return (
        <>
            <div className="card">
                <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-0">
                        <i className="bi bi-calendar-event me-2 text-primary"></i> Custom Hours
                    </h4>
                    <p className="text-muted mb-0">Manage doctor's custom working hours</p>
                </div>
                {!addingNew && (
                    <button className="btn btn-outline-primary" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i> Add Custom Hours
                    </button>
                )}
            </div>

            {loading && customHours.length > 0 ? (
                <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : customHours.length === 0 && !addingNew ? (
                <div className="text-center py-5">
                    <i className="bi bi-calendar-x display-1 text-muted"></i>
                    <h5 className="text-muted mt-3">No custom hours found</h5>
                    <p className="text-muted">Add custom hours to override regular schedule</p>
                    <button className="btn btn-primary mt-2" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i> Add First Custom Hours
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle table-striped">
                        <thead>
                        <tr>
                            <th scope="col">Status</th>
                            <th scope="col" className="ps-4">
                                <i className="bi bi-calendar me-2"></i> Date
                            </th>
                            <th scope="col">Working Hours</th>
                            <th scope="col">Lunch Break</th>

                            <th scope="col" className="text-end pe-4">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {/* Add New Row */}
                        {addingNew && (
                            <tr className="border-top bg-light position-relative">
                                <td>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            {...register('isOff')}
                                        />
                                        <label className="form-check-label small">
                                            {watchIsOff ? 'Day Off' : 'Working'}
                                        </label>
                                    </div>
                                </td>
                                <td className="ps-4">
                                    <input
                                        type="date"
                                        className={`form-control ${errors.workDate ? 'is-invalid' : ''}`}
                                        {...register('workDate', {
                                            required: 'Date is required'
                                        })}
                                    />
                                    {errors.workDate && (
                                        <div className="invalid-feedback">{errors.workDate.message}</div>
                                    )}
                                </td>
                                <td>
                                    {!watchIsOff && (
                                        <div className="d-flex align-items-center g-2">
                                            <div className="">
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    {...register('startTime')}
                                                />
                                            </div>
                                            <div className="ms-2 me-2 align-self-center">
                                                <small className="text-muted">to</small>
                                            </div>
                                            <div className="">
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    {...register('endTime')}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {!watchIsOff && (
                                        <div className="d-flex align-items-center p-1 rounded bg-warning-light g-2">
                                            <div className="">
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    {...register('lunchFrom')}
                                                />
                                            </div>
                                            <div className="ms-1 me-1 align-self-center">
                                                <small className="text-muted">to</small>
                                            </div>
                                            <div className="">
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    {...register('lunchTo')}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </td>
                                <td><small className="text-muted">-</small></td>
                                <td><small className="text-muted">-</small></td>
                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        <button
                                            className="btn btn-success"
                                            onClick={handleSubmit(addCustomHours)}
                                        >
                                            <i className="bi bi-check"></i>
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={handleCancel}
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* Existing Custom Hours */}
                        {customHours.map((customHour) => (
                            <tr key={customHour.id} className="border-top position-relative">
                                {/* Delete Confirmation Overlay */}
                                {deleteConfirm?.id === customHour.id && (
                                    <td colSpan="7" className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-90 d-flex justify-content-between align-items-center px-4" style={{ zIndex: 10 }}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-exclamation-triangle-fill text-danger me-2 fs-4"></i>
                                            <strong>Confirm delete custom hours for {formatDate(customHour.workDate)}?</strong>
                                        </div>
                                        <div className="btn-group">
                                            <button
                                                className="btn btn-danger text-white"
                                                onClick={() => deleteCustomHours(customHour.id)}
                                            >
                                                <i className="bi bi-trash me-1"></i> Confirm
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => setDeleteConfirm(null)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </td>
                                )}

                                <td>
                                    {editingId === customHour.id ? (
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                {...register('isOff')}
                                            />
                                            <label className="form-check-label small">
                                                {watchIsOff ? 'Day Off' : 'Working'}
                                            </label>
                                        </div>
                                    ) : (
                                        <span className={`badge ${customHour.isOff ? 'bg-secondary' : 'bg-success'} text-white fs-6`}>
                                                <i className={`bi ${customHour.isOff ? 'bi-x-circle' : 'bi-check-circle'} me-1`}></i>
                                            {customHour.isOff ? 'Day Off' : 'Working'}
                                            </span>
                                    )}
                                </td>
                                <td className="ps-4">
                                    {editingId === customHour.id ? (
                                        <input
                                            type="date"
                                            className={`form-control ${errors.workDate ? 'is-invalid' : ''}`}
                                            {...register('workDate', {
                                                required: 'Date is required'
                                            })}
                                        />
                                    ) : (
                                        <>
                                            <h6 className="mb-0 fw-semibold">{formatDate(customHour.workDate)}</h6>
                                            <small className="text-muted">ID: {customHour.id.slice(0, 8)}...</small>
                                        </>
                                    )}
                                </td>
                                <td>
                                    {editingId === customHour.id ? (
                                        !watchIsOff && (
                                            <div className="row g-2">
                                                <div className="col">
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        {...register('startTime')}
                                                    />
                                                </div>
                                                <div className="col-auto align-self-center">
                                                    <small className="text-muted">to</small>
                                                </div>
                                                <div className="col">
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        {...register('endTime')}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        customHour.isOff ? (
                                            <span className="text-muted">-</span>
                                        ) : (
                                            <span>
                                                    {formatTime(customHour.startTime)} - {formatTime(customHour.endTime)}
                                                </span>
                                        )
                                    )}
                                </td>
                                <td>
                                    {editingId === customHour.id ? (
                                        !watchIsOff && (
                                            <div className="d-flex align-items-center p-1 rounded bg-warning-light g-2">
                                                <div className="">
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        {...register('lunchFrom')}
                                                    />
                                                </div>
                                                <div className="ms-1 me-1 align-self-center">
                                                    <small className="text-muted">to</small>
                                                </div>
                                                <div className="">
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        {...register('lunchTo')}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        customHour.isOff ? (
                                            <span className="text-muted">-</span>
                                        ) : (
                                            <span>
                                                    {formatTime(customHour.lunchFrom)} - {formatTime(customHour.lunchTo)}
                                                </span>
                                        )
                                    )}
                                </td>

                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        {editingId === customHour.id ? (
                                            <>
                                                <button
                                                    className="btn btn-success"
                                                    onClick={handleSubmit((data) => updateCustomHours(customHour.id, data))}
                                                >
                                                    <i className="bi bi-check"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={handleCancel}
                                                >
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-outline-info"
                                                    onClick={() => handleEdit(customHour)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => setDeleteConfirm(customHour)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted">
                                Showing {customHours.length} of {totalElements} entries
                            </div>
                            <Pagination
                                currentPage={pageNo}
                                totalPages={totalPages}
                                onPageChange={setPageNo}
                            />
                        </div>
                    )}
                </div>
            )}
                </div>
            </div>
        </>
    );
};

export default DoctorCustomHours;